/**
 * Shared pieces of the Appraisal Queue's two views — Internal reviews
 * (AppraisalQueueScreen) and Outside orders (OutsideOrdersQueue): the neutral
 * palette, the stat card, the refresh button, the rail search box, and the
 * tab row that switches between the two.
 */
import { useLocation, useNavigate } from "react-router-dom";

import Box from "@mui/material/Box";
import InputBase from "@mui/material/InputBase";
import RefreshRounded from "@mui/icons-material/RefreshRounded";
import SearchRounded from "@mui/icons-material/SearchRounded";

// The Users screen palette — the tab strip copies its tabs exactly.
import { T as UT } from "../SupportScreen/components/supportUi";
import { slate } from "../../theme/colors";

// Shared neutral palette (mirrors the Lock Queue tokens).
export const T = {
  bg: "#f4f6f8",
  card: "#ffffff",
  line: "#e3e9ec",
  ink900: "#16242b",
  ink700: "#36474f",
  ink500: "#697680",
  ink300: "#aab4bb",
  teal50: "#e6f6f8",
  teal500: "#21a9c4",
  teal600: "#13899f",
  red: "#c0392b",
  radius: "12px",
  shadow: "0 1px 2px rgba(16,24,40,0.04)",
};

export const OUTSIDE_BASE = "/appraisal-queue/outside";

// Same content width as the Users screen (wider than MUI's `xl`) — the queue
// grids carry a lot of columns and the extra room goes straight into them.
export const QUEUE_MAX_WIDTH = "1800px";

/**
 * The loan's correspondent lender (`originator`), joined from the pipeline
 * lists by `withLoanStage` — the queue rows themselves carry only the deal id.
 * Shared by Internal reviews and External orders so the column reads the same
 * on both. `loaded` = the loan index has arrived ("…" until then).
 */
export const correspondentColumnDef = ({ loaded }) => ({
  headerName: "Correspondent",
  colId: "correspondent",
  flex: 1,
  minWidth: 160,
  valueGetter: (p) => p.data?._loan?.originator || null,
  filter: "agSetColumnFilter",
  cellRenderer: (p) => (
    <Box
      title={p.value || ""}
      sx={{
        color: p.value ? T.ink900 : T.ink500,
        minWidth: 0,
        width: "100%",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
    >
      {p.value || (loaded ? "—" : "…")}
    </Box>
  ),
});

export const StatCard = ({ n, label, color }) => (
  <Box
    sx={{
      background: T.card,
      border: `1px solid ${T.line}`,
      borderRadius: T.radius,
      boxShadow: T.shadow,
      p: "16px 18px",
      display: "flex",
      alignItems: "center",
      gap: "14px",
    }}
  >
    <Box
      sx={{
        width: 4,
        alignSelf: "stretch",
        borderRadius: "4px",
        background: color,
      }}
    />
    <Box>
      <Box
        sx={{
          fontSize: 24,
          fontWeight: 800,
          letterSpacing: "-.02em",
          color: T.ink900,
        }}
      >
        {n}
      </Box>
      <Box sx={{ fontSize: 12, color: T.ink500, fontWeight: 500, mt: "1px" }}>
        {label}
      </Box>
    </Box>
  </Box>
);

export const SearchBox = ({ value, onChange, placeholder, sx }) => (
  <Box
    sx={{ position: "relative", display: "flex", alignItems: "center", ...sx }}
  >
    <SearchRounded
      sx={{
        position: "absolute",
        left: 12,
        fontSize: 19,
        color: T.ink300,
        zIndex: 1,
        pointerEvents: "none",
      }}
    />
    <InputBase
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder || "Search property, loan #, requester, assignee…"}
      sx={{
        width: "100%",
        height: 42,
        fontSize: 14,
        pl: "40px",
        pr: "12px",
        border: `1px solid ${T.line}`,
        borderRadius: "9px",
        background: "#fff",
        boxShadow: T.shadow,
        "&.Mui-focused": {
          borderColor: T.teal500,
          boxShadow: "0 0 0 3px rgba(33,169,196,.14)",
        },
      }}
    />
  </Box>
);

export const RefreshButton = ({ onClick, loading }) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    disabled={loading}
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: "8px",
      font: "inherit",
      fontSize: 13.5,
      fontWeight: 600,
      color: T.ink700,
      background: "#fff",
      border: `1px solid ${T.line}`,
      borderRadius: "8px",
      boxShadow: T.shadow,
      px: "14px",
      py: "9px",
      cursor: loading ? "default" : "pointer",
      opacity: loading ? 0.6 : 1,
    }}
  >
    <RefreshRounded sx={{ fontSize: 18 }} />
    Refresh
  </Box>
);

const QUEUE_TABS = [
  { value: "internal", label: "Internal reviews", to: "/appraisal-queue" },
  { value: "outside", label: "External orders", to: OUTSIDE_BASE },
];

/**
 * Internal reviews ↔ External orders — the same underline strip as the Users
 * screen's tabs (bold dark label + teal underline on the active one).
 */
export const AppraisalQueueTabs = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const value = pathname.startsWith(OUTSIDE_BASE) ? "outside" : "internal";
  return (
    <Box
      role="tablist"
      sx={{
        display: "flex",
        gap: 3,
        // One step darker than the Users strip so the baseline reads on the gray page.
        borderBottom: `1px solid ${slate[300]}`,
        mt: 2.5,
      }}
    >
      {QUEUE_TABS.map((o) => {
        const on = value === o.value;
        return (
          <Box
            key={o.value}
            component="button"
            type="button"
            role="tab"
            aria-selected={on}
            onClick={() => navigate(o.to)}
            sx={{
              font: "inherit",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              border: "none",
              background: "transparent",
              cursor: "pointer",
              px: "2px",
              py: "10px",
              mb: "-1px",
              color: on ? UT.ink900 : UT.ink500,
              fontWeight: on ? 700 : 600,
              fontSize: 14,
              borderBottom: `2px solid ${on ? UT.teal500 : "transparent"}`,
            }}
          >
            {o.label}
          </Box>
        );
      })}
    </Box>
  );
};
